import { ModeratorPermissionsResponse, PermissionGroup, ModeratorPermission, MetricPermissionModel } from "../../types/api/moderators";
import { MetricType } from '../../types/api/metrics/metricsType';

export const permissionGroups: PermissionGroup[] = [
    {
        title: "Пользователи",
        items: [
            { key: ModeratorPermission.CAN_VIEW_USERS, label: "Просмотр списка пользователей" },
        ]
    },
    {
        title: "Карточка пользователя",
        items: [
            { key: ModeratorPermission.CAN_VIEW_USERS, label: "Просмотр карточки пользователя" },
            { key: ModeratorPermission.CAN_BLOCK_USERS, label: "Блокировка/разблокировка пользователей" },
        ]
    },
    {
        title: "Объявления",
        items: [
            { key: ModeratorPermission.CAN_VIEW_ADS, label: "Просмотр списка объявлений" },
        ]
    },
    {
        title: "Карточка объявления",
        items: [
            { key: ModeratorPermission.CAN_VIEW_ADS, label: "Просмотр карточки объявления" },
            { key: ModeratorPermission.CAN_BLOCK_ADS, label: "Блокировка объявления" },
        ]
    },
    {
        title: "Объявления требующие подтверждения",
        items: [
            { key: ModeratorPermission.CAN_VIEW_ADS, label: "Просмотр карточки объявления" },
            { key: ModeratorPermission.CAN_BLOCK_ADS, label: "Блокировка объявления" },
            { key: ModeratorPermission.CAN_APPROVE_ADS, label: "Подтверждение объявления" },
        ]
    },
    {
        title: "Обращения пользователей",
        items: [
            { key: ModeratorPermission.CAN_VIEW_APPEALS, label: "Просмотр обращений пользователей" },
            { key: ModeratorPermission.CAN_REPLY_APPEALS, label: "Ответ на обращение пользователя" },
        ]
    },
    {
        title: "Предложения пользователей",
        items: [
            { key: ModeratorPermission.CAN_VIEW_PROPOSALS, label: "Просмотр предложений пользователей" },
        ]
    },
    {
        title: "Жалобы пользователей",
        items: [
            { key: ModeratorPermission.CAN_VIEW_COMPLAINTS, label: "Просмотр жалоб пользователей" },
        ]
    },
    {
        title: "Дашборд по метрикам",
        items: [
            // Флаг в поле поставится автоматически при наличии выбранных метрик (MetricPermissionModel)
            //{ key: ModeratorPermission.CAN_VIEW_DASHBOARD_METRICS, label: "Просмотр доступных метрик" },
        ]
    }
];

const permissionMapping = {
    canViewUsers: ModeratorPermission.CAN_VIEW_USERS,
    canBlockUsers: ModeratorPermission.CAN_BLOCK_USERS,
    canViewAds: ModeratorPermission.CAN_VIEW_ADS,
    canBlockAds: ModeratorPermission.CAN_BLOCK_ADS,
    canApproveAds: ModeratorPermission.CAN_APPROVE_ADS,
    canViewAppeals: ModeratorPermission.CAN_VIEW_APPEALS,
    canReplyAppeals: ModeratorPermission.CAN_REPLY_APPEALS,
    canViewProposals: ModeratorPermission.CAN_VIEW_PROPOSALS,
    canViewComplaints: ModeratorPermission.CAN_VIEW_COMPLAINTS,
    canViewDashboardMetrics: ModeratorPermission.CAN_VIEW_DASHBOARD_METRICS,
} as const;


export const createPermissionsFromSetDynamic = (
    selectedPermissions: Set<ModeratorPermission>,
    selectedMetrics: MetricPermissionModel
): ModeratorPermissionsResponse => {
    const permissionMap: Record<ModeratorPermission, keyof Omit<ModeratorPermissionsResponse, 'metricsPermissions'>> = {
        [ModeratorPermission.CAN_VIEW_USERS]: 'canViewUsers',
        [ModeratorPermission.CAN_BLOCK_USERS]: 'canBlockUsers',
        [ModeratorPermission.CAN_VIEW_ADS]: 'canViewAds',
        [ModeratorPermission.CAN_BLOCK_ADS]: 'canBlockAds',
        [ModeratorPermission.CAN_APPROVE_ADS]: 'canApproveAds',
        [ModeratorPermission.CAN_VIEW_APPEALS]: 'canViewAppeals',
        [ModeratorPermission.CAN_REPLY_APPEALS]: 'canReplyAppeals',
        [ModeratorPermission.CAN_VIEW_PROPOSALS]: 'canViewProposals',
        [ModeratorPermission.CAN_VIEW_COMPLAINTS]: 'canViewComplaints',
        [ModeratorPermission.CAN_VIEW_DASHBOARD_METRICS]: 'canViewDashboardMetrics',
    };

    const result = Object.values(ModeratorPermission).reduce((acc, permission) => {
        const responseKey = permissionMap[permission];
        acc[responseKey] = selectedPermissions.has(permission);
        return acc;
    }, {} as Omit<ModeratorPermissionsResponse, 'metricsPermissions'>);

    const metricsPermissions: MetricPermissionModel = Object.values(MetricType).reduce(
        (acc, metric) => {
            acc[metric] = selectedMetrics[metric] ?? false;
            return acc;
        },
        {} as MetricPermissionModel
    );

    return {
        ...result,
        metricsPermissions,
    };
};
// Функция для получения активных разрешений из объекта permissions
function getActivePermissions(permissions: ModeratorPermissionsResponse): ModeratorPermission[] {
    return Object.entries(permissions)
        .filter(([_, value]) => value === true)
        .map(([key, _]) => permissionMapping[key as keyof typeof permissionMapping]);
}


// Функция для фильтрации групп разрешений по активным разрешениям
export function getPermissionGroups(permissions: ModeratorPermissionsResponse): PermissionGroup[] {
    const activePermissions = getActivePermissions(permissions);

    return permissionGroups
        .map(group => ({
            ...group,
            items: group.items.filter(item => activePermissions.includes(item.key))
        }))
        .filter(group => group.items.length > 0);
}

export const getSelectedPermissionsLabels = (permissionGroups: PermissionGroup[], selectedPermissions: Set<ModeratorPermission>): string => {
    const labels = new Set<string>();

    permissionGroups.forEach(group => {
        group.items.forEach(item => {
            if (selectedPermissions.has(item.key)) {
                labels.add(item.label);
            }
        });
    });

    return Array.from(labels).join(', ');
};